// frontend/src/components/dialogs/SaveTemplateDialog.js
import React from 'react';
import { Save, X, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import ConfirmDialogShell from './ConfirmDialogShell';

/**
 * Specialized dialog for confirming template save operations
 * Uses the standardized ConfirmDialogShell with save-specific messaging
 */
const SaveTemplateDialog = ({
    isOpen,
    onClose,
    onConfirm,
    isSaving = false,
    eventType = '',
    channel = '',
    hasChanges = true
}) => {
    const { t } = useTranslation();
    const { text, semantic, cn } = useTheme();

    return (
        <ConfirmDialogShell
            isOpen={isOpen}
            onClose={onClose}
            onConfirm={onConfirm}
            title={t('saveTemplateTitle')}
            confirmLabel={t('saveTemplate')}
            cancelLabel={t('Cancel')}
            confirmVariant="primary"
            confirmIcon={Save}
            cancelIcon={X}
            isLoading={isSaving}
            showIcon={true}
            iconVariant="success"
            size="md"
        >
            <div className="space-y-4">
                {/* Primary confirmation message */}
                <div className="text-center">
                    <p className={cn('font-medium mb-2', text.primary)}>
                        {t('saveTemplateWarning')}
                    </p>
                    <p className={cn('text-sm', text.secondary)}>
                        {t('saveTemplateConsequence')}
                    </p>
                </div>

                {/* Template details */}
                {(eventType || channel) && (
                    <div className={cn('p-4 rounded-lg border', semantic.info)}>
                        <div className={cn('text-sm space-y-1', text.primary)}>
                            <div className="font-medium">{t('Template Details')}:</div>
                            {eventType && (
                                <div>• {t('eventType')}: {eventType}</div>
                            )}
                            {channel && (
                                <div>• {t('channel')}: {t(`${channel}Channel`) || channel}</div>
                            )}
                        </div>
                    </div>
                )}

                {/* Impact information */}
                <div className={cn('text-xs p-3 rounded border-l-4 border-blue-400', semantic.info)}>
                    <p className={cn('font-medium mb-1', text.primary)}>
                        💡 {t('saveTemplateImpact')}:
                    </p>
                    <ul className={cn('space-y-1', text.secondary)}>
                        <li>• {t('All future notifications')} {channel ? `(${channel})` : ''}</li>
                        <li>• {t('Users will receive')}</li>
                        <li>• {t('Review changes carefully')}</li>
                    </ul>
                </div>

                {/* Changes indicator */}
                {hasChanges && (
                    <div className={cn('text-center text-xs', text.muted)}>
                        ✨ {t('You have unsaved changes')}
                    </div>
                )}
            </div>
        </ConfirmDialogShell>
    );
};

export default SaveTemplateDialog;